import React, { useState } from 'react';
import './CryptoLogo.css';
import {
  tokenLogoUrls,
  chainLogoUrls,
  tokenColor,
  tokenInitials,
  chainDisplayLabel,
  isUsableLogoUrl,
} from '../../utils/assetLogos';

/**
 * <img> that walks a list of candidate URLs, then falls back to a colored
 * letter glyph once every source has failed to load. Keeps logos resilient
 * even when a CDN entry is missing for an exotic symbol/chain.
 */
const FallbackImg = ({ sources, alt, size, glyph, glyphColor }) => {
  const [idx, setIdx] = useState(0);
  // Drop placeholder / hotlink-blocked urls up front. A url that renders a
  // generic "unknown" image satisfies <img> and would stop the walk before the
  // real CDN is ever tried.
  const valid = sources.flat().filter(isUsableLogoUrl);

  if (idx < valid.length) {
    return (
      <img
        className="crypto-logo"
        src={valid[idx]}
        alt={alt}
        width={size}
        height={size}
        loading="lazy"
        onError={() => setIdx(idx + 1)}
      />
    );
  }
  return (
    <span
      className="crypto-logo crypto-logo--glyph"
      style={{ background: glyphColor, width: size, height: size, fontSize: Math.round(size * 0.38) }}
      aria-hidden="true"
    >
      {glyph}
    </span>
  );
};

/** Round token logo (DB logoUrl → icon CDNs → colored glyph). */
export const TokenLogo = ({ symbol, logoUrl, size = 28 }) => (
  <FallbackImg
    sources={[logoUrl, ...tokenLogoUrls(symbol)]}
    alt={symbol ? `${symbol} logo` : 'token'}
    size={size}
    glyph={tokenInitials(symbol)}
    glyphColor={tokenColor(symbol)}
  />
);

/** Round chain logo (DB logoUrl → DefiLlama → native asset icon → glyph). */
export const ChainLogo = ({ chain, logoUrl, size = 16 }) => {
  const label = chainDisplayLabel(chain);
  return (
    <FallbackImg
      sources={[logoUrl, ...chainLogoUrls(chain)]}
      alt={label ? `${label} logo` : 'chain'}
      size={size}
      glyph={(label || '?').charAt(0).toUpperCase()}
      glyphColor="var(--t-500)"
    />
  );
};

/**
 * CMC-style composite: token logo with a small chain logo badge in the
 * bottom-right corner (e.g. "USDT on BNB"). When no chain is given, renders
 * just the token logo.
 */
export const TokenChainLogo = ({
  symbol,
  tokenLogoUrl: tUrl,
  chain,
  chainLogoUrl: cUrl,
  size = 28,
  showChain = true,
}) => {
  const hasChain = showChain && chain && chainDisplayLabel(chain);
  const badge = Math.round(size * 0.46);
  return (
    <span className="crypto-logo-stack" style={{ width: size, height: size }}>
      <TokenLogo symbol={symbol} logoUrl={tUrl} size={size} />
      {hasChain ? (
        <span className="crypto-logo-stack__chain" style={{ width: badge, height: badge }}>
          <ChainLogo chain={chain} logoUrl={cUrl} size={badge} />
        </span>
      ) : null}
    </span>
  );
};

export default TokenChainLogo;
